import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

/**
 * PedigreeTable — renders the pedigree chart from display data.
 *
 * Props:
 *   data: { rows, colors, horse_colors, horse_name }
 *         (same shape returned by /api/horses/<id>/pedigree/display
 *          and /api/breeding/hypothetical's `display` field)
 *   horseType: optional string like 'mare', 'stallion', 'filly', 'colt'.
 *              Controls the foal cell's background color. Defaults to
 *              filly/mare pink if not given. Pass null to keep that default.
 *   fillHeight: when true, scale the table uniformly to fill the container height/width
 */

const TABLE_COLUMNS = 6;

const getCellClassName = (cellClass) => {
  const classMap = {
    m: 'pedigree-male',
    f: 'pedigree-female',
    foal: 'pedigree-foal',
  };
  return classMap[cellClass] || '';
};

const getColorStyle = (color) => {
  // For horse names: blue for single-occurrence horses, the assigned color for duplicates
  if (color === 'black') return { color: '#0000ff' };
  const colorMap = {
    darkblue: '#00008B',
    red: '#FF0000',
    white: '#FFFFFF',
    aqua: '#00FFFF',
    green: '#008000',
    darkteal: '#008080',
    olive: '#808000',
    deepteal: '#004D4D',
    navy: '#000080',
    blue: '#0000FF',
    teal: '#008080',
    purple: '#800080',
    maroon: '#800000',
  };
  return { color: colorMap[color] || '#0000ff' };
};

const tableSx = (foalBg, fillHeight) => ({
  margin: 0,
  padding: 0,
  '& .pedigree-table': {
    borderCollapse: 'collapse',
    width: fillHeight ? 'max-content' : '100%',
    minWidth: fillHeight ? '100%' : undefined,
    border: '2px solid #000000',
    fontSize: '7pt',
    margin: 0,
  },
  '& .pedigree-male': {
    backgroundColor: '#ccccff',
    color: '#0000ff',
    fontFamily: 'Verdana, sans-serif',
    fontSize: '9.33px',
    textAlign: 'center',
    verticalAlign: 'middle',
    border: '1px solid #000000',
    padding: '1px 2px',
    lineHeight: '1.1',
  },
  '& .pedigree-female': {
    backgroundColor: '#ffcccc',
    color: '#0000ff',
    fontFamily: 'Verdana, sans-serif',
    fontSize: '9.33px',
    textAlign: 'center',
    verticalAlign: 'middle',
    border: '1px solid #000000',
    padding: '1px 2px',
    lineHeight: '1.1',
  },
  '& .pedigree-foal': {
    backgroundColor: foalBg,
    color: '#0000ff',
    fontFamily: 'Verdana, sans-serif',
    fontSize: '9.33px',
    textAlign: 'center',
    verticalAlign: 'middle',
    border: '1px solid #000000',
    padding: '1px 2px',
    lineHeight: '1.1',
  },
});

const PedigreeTable = ({ data, horseType, fillHeight = false }) => {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [layoutSize, setLayoutSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    if (!fillHeight) {
      setScale(1);
      setLayoutSize({ width: 0, height: 0 });
      return undefined;
    }

    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return undefined;

    const updateScale = () => {
      const cw = container.clientWidth;
      const ch = container.clientHeight;
      const tw = content.scrollWidth;
      const th = content.scrollHeight;
      if (!cw || !ch || !tw || !th) return;

      const nextScale = Math.max(1, Math.min(cw / tw, ch / th));
      setScale(nextScale);
      setLayoutSize({ width: tw * nextScale, height: th * nextScale });
    };

    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(container);
    return () => observer.disconnect();
  }, [fillHeight, data]);

  if (!data || !data.rows || data.rows.length === 0) return null;

  const foalBg = (() => {
    const type = (horseType || '').toLowerCase();
    return type === 'mare' || type === 'filly' ? '#ccccff' : '#ffcccc';
  })();

  const table = (
    <table className="pedigree-table" border="1" cellPadding="0" cellSpacing="0">
      <tbody>
        {data.rows.map((row, rowIndex) => {
          const rowLength = row.cells.length;
          return (
            <tr key={rowIndex}>
              {row.cells.map((cell, cellIndex) => {
                const cellColspan = cell.colspan || 1;
                const columnIndex = TABLE_COLUMNS - rowLength + cellIndex;
                return (
                  <td
                    key={`${rowIndex}-${cellIndex}`}
                    rowSpan={cell.rowspan}
                    colSpan={cellColspan}
                    className={getCellClassName(cell.class)}
                    style={{
                      width: cell.width,
                      ...getColorStyle(cell.color),
                    }}
                  >
                    {cell.name !== '&nbsp;' && (
                      <>
                        <div
                          style={{
                            fontSize: '9.33px',
                            lineHeight: '1.1',
                            ...getColorStyle(cell.color),
                          }}
                        >
                          {cell.name}
                        </div>
                        {cell.details && cell.details.trim() && (
                          <div
                            style={{
                              fontSize: '9.33px',
                              lineHeight: '1.1',
                              marginTop: '1px',
                              color: '#000000',
                            }}
                          >
                            {cell.details}
                          </div>
                        )}
                        {columnIndex < 4 && cell.breed && cell.breed.trim() && (
                          <div
                            style={{
                              fontSize: '9.33px',
                              lineHeight: '1.1',
                              marginTop: '1px',
                              color: '#000000',
                            }}
                          >
                            {cell.breed}
                          </div>
                        )}
                        {columnIndex < 4 && cell.reg && cell.reg.trim() && (
                          <div
                            style={{
                              fontSize: '9.33px',
                              lineHeight: '1.1',
                              marginTop: '1px',
                              color: '#000000',
                            }}
                          >
                            {cell.reg}
                          </div>
                        )}
                      </>
                    )}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        overflow: fillHeight ? 'hidden' : 'auto',
        height: fillHeight ? '100%' : 'auto',
        ...(fillHeight
          ? {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }
          : {}),
        ...tableSx(foalBg, fillHeight),
      }}
    >
      {fillHeight ? (
        <Box
          sx={{
            width: layoutSize.width || 'auto',
            height: layoutSize.height || 'auto',
            overflow: 'hidden',
          }}
        >
          <Box
            ref={contentRef}
            sx={{
              display: 'inline-block',
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
            }}
          >
            {table}
          </Box>
        </Box>
      ) : (
        table
      )}
    </Box>
  );
};

export default PedigreeTable;
